import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard, CalendarCheck, GraduationCap, ShoppingBag,
  MessageSquare, Smartphone, Users, ChevronLeft, ChevronRight,
  LogOut, Menu, X, ExternalLink, FileText
} from 'lucide-react';

const LOGO_URL = '/logo-givinghopes.png';

const sidebarLinks = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/bookings', icon: CalendarCheck, label: 'Bookings' },
  { to: '/admin/courses', icon: GraduationCap, label: 'Courses' },
  { to: '/admin/products', icon: ShoppingBag, label: 'Products' },
  { to: '/admin/blog', icon: FileText, label: 'Blog Posts' },
  { to: '/admin/contacts', icon: MessageSquare, label: 'Contacts' },
  { to: '/admin/app-leads', icon: Smartphone, label: 'App Leads' },
  { to: '/admin/subscribers', icon: Users, label: 'Subscribers' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const SidebarContent = () => (
    <>
      {/* Brand */}
      <div className="px-4 pt-6 pb-4 flex items-center gap-3">
        <img src={LOGO_URL} alt="Logo" className="h-9 w-9 object-contain flex-shrink-0" />
        {!collapsed && (
          <span className="text-white font-heading font-bold text-sm tracking-wide whitespace-nowrap">
            GIVING HOPES
          </span>
        )}
      </div>

      {/* User Info */}
      <div className={`px-4 pb-6 ${collapsed ? 'text-center' : ''}`}>
        <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
            {user?.name?.charAt(0) || 'A'}
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-white/90 text-sm font-semibold truncate">{user?.name || 'Admin'}</p>
              <p className="text-white/50 text-xs">Super Admin</p>
            </div>
          )}
        </div>
      </div>

      {/* Nav Links */}
      <nav className="flex-1 px-3 space-y-1">
        {sidebarLinks.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.end}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 group ${
                isActive
                  ? 'bg-white/15 text-white font-semibold'
                  : 'text-white/60 hover:text-white hover:bg-white/8'
              } ${collapsed ? 'justify-center' : ''}`
            }
            data-testid={`admin-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <link.icon size={18} className="flex-shrink-0" />
            {!collapsed && <span>{link.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Bottom */}
      <div className="px-3 pb-4 space-y-1">
        <a
          href="/"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/50 hover:text-white hover:bg-white/8 transition-all ${collapsed ? 'justify-center' : ''}`}
          data-testid="admin-nav-website"
        >
          <ExternalLink size={18} />
          {!collapsed && <span>View Website</span>}
        </a>
        <button
          onClick={handleLogout}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/50 hover:text-red-400 hover:bg-red-500/10 transition-all w-full ${collapsed ? 'justify-center' : ''}`}
          data-testid="admin-logout-btn"
        >
          <LogOut size={18} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-[#0c1929]" data-testid="admin-layout">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed top-0 left-0 bottom-0 z-40 transition-all duration-300 ${
          collapsed ? 'w-[72px]' : 'w-[240px]'
        }`}
        style={{ background: 'linear-gradient(180deg, #0d2137 0%, #0a3d5c 50%, #0d4a4e 100%)' }}
      >
        <SidebarContent />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-[#1a3a5c] border border-white/20 rounded-full flex items-center justify-center text-white/70 hover:text-white z-50"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside
            className="relative w-[260px] flex flex-col"
            style={{ background: 'linear-gradient(180deg, #0d2137 0%, #0a3d5c 50%, #0d4a4e 100%)' }}
          >
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${collapsed ? 'lg:ml-[72px]' : 'lg:ml-[240px]'}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-[#0f2540]/95 backdrop-blur-md border-b border-white/5 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden text-white/70 hover:text-white"
              onClick={() => setMobileOpen(true)}
              data-testid="admin-mobile-menu"
            >
              <Menu size={22} />
            </button>
            <h2 className="text-white/90 font-heading font-bold text-sm hidden sm:block">
              Giving Hope Admin Panel
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-white/50 text-xs">{user?.email}</span>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
